/** Optimization API: `dataset_name` is a label string or an array of labels. */

export type OptimizationDatasetNamePayload = string | string[];

export function normalizeOptimizationDatasetNames(value: unknown): string[] {
  if (Array.isArray(value)) {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of value) {
      if (typeof item !== 'string') continue;
      const trimmed = item.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      out.push(trimmed);
    }
    return out;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  return [];
}

/** Build request field: string for one dataset, array for several. Undefined if empty (400 on API). */
export function formatOptimizationDatasetNameForApi(
  names: string[]
): OptimizationDatasetNamePayload | undefined {
  const list = normalizeOptimizationDatasetNames(names);
  if (list.length === 0) return undefined;
  if (list.length === 1) return list[0];
  return list;
}

export function formatOptimizationDatasetDisplay(
  value: string | string[] | null | undefined
): string {
  const list = normalizeOptimizationDatasetNames(value);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return list.join(', ');
}

export function hasOptimizationDatasetSelection(
  value: string | string[] | null | undefined
): boolean {
  return normalizeOptimizationDatasetNames(value).length > 0;
}

/** API defaults: `{ default, options }` or legacy string array. */
export function parseDefaultsDatasetConfig(datasets: unknown): {
  options: string[];
  defaultName: string;
} {
  if (datasets && typeof datasets === 'object' && !Array.isArray(datasets)) {
    const record = datasets as { options?: unknown; default?: unknown };
    const options = normalizeOptimizationDatasetNames(record.options);
    const rawDefault = typeof record.default === 'string' ? record.default.trim() : '';
    const defaultName =
      rawDefault && options.includes(rawDefault) ? rawDefault : options[0] || '';
    return { options, defaultName };
  }
  if (Array.isArray(datasets)) {
    const options = normalizeOptimizationDatasetNames(datasets);
    return { options, defaultName: options[0] || '' };
  }
  return { options: [], defaultName: '' };
}
